import { makeAutoObservable, runInAction } from 'mobx';
import docsAgent, { WikiEntry, isDocsRequestCanceled } from '../services/api/docsAgent';

export type WikiEntryStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface WikiEntryState {
  status: WikiEntryStatus;
  data?: WikiEntry;
  error?: unknown;
  fetchedAt?: number;
}

interface LoadOptions {
  force?: boolean;
  signal?: AbortSignal;
}

const DEFAULT_ENTRY_TTL_MS = 3 * 60 * 60 * 1000;

export default class DocsStore {
  entries = new Map<string, WikiEntryState>();
  private inflightLoads = new Map<string, Promise<WikiEntry | undefined>>();
  wikiEntries: WikiEntry[] = [];
  wikiEntriesStatus: WikiEntryStatus = 'idle';
  wikiEntriesError?: unknown;
  wikiEntriesFetchedAt?: number;
  private wikiEntriesLoad?: Promise<WikiEntry[] | undefined>;
  entryTtlMs = DEFAULT_ENTRY_TTL_MS;

  constructor() {
    makeAutoObservable(this);
  }

  getEntry = (reactName: string) => {
    return this.entries.get(reactName);
  };

  private isStale = (entry?: WikiEntryState) => {
    if (!entry || !entry.fetchedAt) {
      return true;
    }
    return Date.now() - entry.fetchedAt > this.entryTtlMs;
  };

  private isListStale = () => {
    if (!this.wikiEntriesFetchedAt) {
      return true;
    }
    return Date.now() - this.wikiEntriesFetchedAt > this.entryTtlMs;
  };

  private cacheEntry = (entry: WikiEntry, previousReactName?: string) => {
    if (previousReactName && previousReactName !== entry.reactName) {
      this.entries.delete(previousReactName);
    }
    this.entries.set(entry.reactName, {
      status: 'ready',
      data: entry,
      fetchedAt: Date.now(),
    });
  };

  load = async (reactName: string, options: LoadOptions = {}) => {
    const key = reactName.trim();
    if (!key) {
      return undefined;
    }

    const existing = this.entries.get(key);
    const { force = false, signal } = options;

    if (!force && existing?.status === 'ready' && existing.data && !this.isStale(existing)) {
      return existing.data;
    }

    if (!force && this.inflightLoads.has(key)) {
      return this.inflightLoads.get(key);
    }

    runInAction(() => {
      this.entries.set(key, {
        status: 'loading',
        data: existing?.data,
        fetchedAt: existing?.fetchedAt,
      });
    });

    const loadPromise = docsAgent.WikiEntries.getByReactName(key, signal)
      .then((entry) => {
        runInAction(() => {
          this.entries.set(key, {
            status: 'ready',
            data: entry,
            fetchedAt: Date.now(),
          });
        });
        return entry;
      })
      .catch((error) => {
        if (isDocsRequestCanceled(error)) {
          runInAction(() => {
            if (existing) {
              this.entries.set(key, existing);
            } else {
              this.entries.delete(key);
            }
          });
          return undefined;
        }

        runInAction(() => {
          this.entries.set(key, {
            status: 'error',
            data: existing?.data,
            fetchedAt: Date.now(),
            error,
          });
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.inflightLoads.delete(key);
        });
      });

    this.inflightLoads.set(key, loadPromise);

    return loadPromise;
  };

  listEntries = async (options: LoadOptions = {}) => {
    const { force = false, signal } = options;
    if (!force && this.wikiEntriesStatus === 'ready' && !this.isListStale()) {
      return this.wikiEntries;
    }
    if (!force && this.wikiEntriesLoad) {
      return this.wikiEntriesLoad;
    }

    runInAction(() => {
      this.wikiEntriesStatus = 'loading';
      this.wikiEntriesError = undefined;
    });

    const loadPromise = docsAgent.WikiEntries.list(undefined, signal)
      .then((entries) => {
        runInAction(() => {
          this.wikiEntries = entries;
          this.wikiEntriesStatus = 'ready';
          this.wikiEntriesFetchedAt = Date.now();
          entries.forEach((entry) => this.cacheEntry(entry));
        });
        return entries;
      })
      .catch((error) => {
        if (isDocsRequestCanceled(error)) {
          runInAction(() => {
            this.wikiEntriesStatus = this.wikiEntries.length ? 'ready' : 'idle';
          });
          return undefined;
        }
        runInAction(() => {
          this.wikiEntriesError = error;
          this.wikiEntriesStatus = 'error';
          this.wikiEntriesFetchedAt = Date.now();
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.wikiEntriesLoad = undefined;
        });
      });

    this.wikiEntriesLoad = loadPromise;
    return loadPromise;
  };

  createEntry = async (
    payload: { url: string; spanIndex: number; reactName: string; lastUpdatedAt?: string },
    signal?: AbortSignal,
  ) => {
    const entry = await docsAgent.WikiEntries.create(payload, signal);
    runInAction(() => {
      this.wikiEntries = [entry, ...this.wikiEntries];
      this.wikiEntriesStatus = 'ready';
      this.wikiEntriesFetchedAt = Date.now();
      this.cacheEntry(entry);
    });
    return entry;
  };

  updateEntry = async (id: number, payload: Partial<Omit<WikiEntry, 'wikiEntryID'>>, signal?: AbortSignal) => {
    const existing = this.wikiEntries.find((entry) => entry.wikiEntryID === id);
    const updated = await docsAgent.WikiEntries.update(id, payload, signal);
    runInAction(() => {
      this.wikiEntries = this.wikiEntries.map((entry) => (entry.wikiEntryID === id ? updated : entry));
      this.cacheEntry(updated, existing?.reactName);
    });
    return updated;
  };

  forceUpdateEntries = async (payload: { url: string; reactNames?: string[] }, signal?: AbortSignal) => {
    const updatedEntries = await docsAgent.WikiEntries.forceUpdate(payload, signal);
    runInAction(() => {
      if (!updatedEntries.length) {
        return;
      }
      const updatesById = new Map(updatedEntries.map((entry) => [entry.wikiEntryID, entry]));
      this.wikiEntries = this.wikiEntries.map((entry) => {
        const updated = updatesById.get(entry.wikiEntryID);
        if (!updated) {
          return entry;
        }
        updatesById.delete(entry.wikiEntryID);
        this.cacheEntry(updated, entry.reactName);
        return updated;
      });
      updatesById.forEach((entry) => {
        this.wikiEntries.push(entry);
        this.cacheEntry(entry);
      });
      this.wikiEntriesStatus = 'ready';
      this.wikiEntriesFetchedAt = Date.now();
    });
    return updatedEntries;
  };

  refresh = async (reactName: string, signal?: AbortSignal) => {
    return this.load(reactName, { force: true, signal });
  };

  preload = (reactNames: string[]) => {
    return Promise.all(reactNames.map((name) => this.load(name)));
  };

  clear = () => {
    runInAction(() => {
      this.entries.clear();
      this.inflightLoads.clear();
      this.wikiEntries = [];
      this.wikiEntriesStatus = 'idle';
      this.wikiEntriesError = undefined;
      this.wikiEntriesFetchedAt = undefined;
      this.wikiEntriesLoad = undefined;
    });
  };
}
